/**
 * Item Handlers — pushes an inline ItemConfirmationBlock into task messages
 * when a skill / agent / ability is created from within a task conversation.
 *
 * Mirrors routineHandlers: the server ALSO persists this block (buffered on
 * ITEM_CREATED_IN_TASK), so the live-appended copy must not double-render
 * against the persisted-on-reload copy. The committed-message path uses a
 * deterministic id (`msg-item-${itemId}`) so MESSAGE_SYNC's server-authoritative
 * replacement discards the optimistic copy on reload, and a duplicate live
 * event resolves to the same message instead of a second card.
 */

import type { Socket } from "socket.io-client";
import { EVENTS, type ItemConfirmationBlock } from "@bash/shared";
import { useTaskStore } from "../taskStore";

interface ItemCreatedInTaskPayload {
  taskId: string;
  itemType: "skill" | "agent" | "ability";
  // Wire payload carries `id`; the block field is `itemId`.
  id: string;
  slug: string;
  name: string;
  icon?: string;
  color?: string;
  abilitySlug: string;
}

export function registerItemHandlers(socket: Socket): () => void {
  const taskStore = useTaskStore;

  const handleItemCreatedInTask = (data: ItemCreatedInTaskPayload) => {
    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(data.taskId);
      if (!task) return;

      const block: ItemConfirmationBlock = {
        type: "item_confirmation",
        itemType: data.itemType,
        itemId: data.id,
        slug: data.slug,
        name: data.name,
        icon: data.icon,
        color: data.color,
        abilitySlug: data.abilitySlug,
      };

      if (task.streamingMessage) {
        // Agent is actively streaming — append to current message.
        task.streamingMessage.content.push(block);
      } else {
        // Agent turn already ended — add directly to committed messages.
        const itemClientId = `msg-item-${data.id}`;
        // Guard against a duplicate live delivery: the deterministic id means
        // the same item never produces a second card.
        if (task.messages.some((m) => m.id === itemClientId)) return;
        task.messages = [
          ...task.messages,
          {
            id: itemClientId,
            role: "assistant" as const,
            content: [block],
            timestamp: new Date(),
            agentSlug: task.agentSlug || "bash",
            identity: { kind: "optimistic", clientId: itemClientId },
          },
        ];
      }
    });
  };

  socket.on(EVENTS.ITEM_CREATED_IN_TASK, handleItemCreatedInTask);

  return () => {
    socket.off(EVENTS.ITEM_CREATED_IN_TASK, handleItemCreatedInTask);
  };
}
